import React from 'react';

import classNames, { Argument } from 'classnames';

import { Props as BadgeProps } from '../Badge';

export type FormControlProps<T> = {
  value?: T;
  onChange?: (isChecked: T) => void;
  label?: string | React.ReactElement;
  indeterminate?: boolean;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'>;

export type FormControlListOption<T> = {
  value: T;
  label?: string;
  disabled?: boolean;
  default?: boolean;
  tooltip?: string;
  badge?: BadgeProps;
};

export type FormControlListProps<GlobalValue, ItemValue = GlobalValue> = {
  className?: string;
  optionsClassName?: string;
  inline?: boolean;
  options: FormControlListOption<ItemValue>[];
  id?: string;
  disabled?: boolean;
  onChange: (value: GlobalValue) => void;
  value: GlobalValue;
  renderingOptions?: {
    containerClassName?: string;
    elementClassName?: string;
  };
  error?: string;
};

export type FormControlListOptionVerticalChip<T> = FormControlListOption<T> & {
  centerIcon: React.ReactElement;
};

export type FormControlListVerticalChipProps<GlobalValue, ItemValue = GlobalValue> = Omit<
  FormControlListProps<GlobalValue, ItemValue>,
  'options'
> & {
  options: FormControlListOptionVerticalChip<ItemValue>[];
};

export const commonFormControlContainerClasses = (
  checked?: boolean,
  disabled?: boolean,
  error?: boolean,
): Argument[] => [
  'outline outline-1 outline-solid outline-disabled m-3xs text-inverse peer-focus-visible:outline-2 peer-focus-visible:outline-info group-active:action-outline-primary-pressed group-focus:outline-info group-focus:outline-2 group-hover:action-outline-primary-hover',
  { 'group-focus:outline-2 group-focus:outline-info': !disabled },
  {
    'outline-brand! group-hover:action-outline-primary-hover! group-active:action-outline-primary-pressed!':
      !disabled && checked,
  },
  { 'outline-danger!': !disabled && error },
  { 'group-hover:outline-neutral group-active:outline-brand': !disabled && !checked },
];

export const commonFormControlCheckmarkClasses = (
  checked?: boolean,
  disabled?: boolean,
  indeterminate?: boolean,
): Argument[] => [
  { 'ctn-neutral-grey': disabled && !checked },
  { 'ctn-neutral-grey-strong': disabled && checked },
  {
    'action-bg-primary-default group-hover:action-bg-primary-hover group-active:action-bg-primary-pressed':
      !disabled && (checked || indeterminate),
  },
];

export const hiddenInputClassNames = 'peer cursor-pointer absolute top-0 left-0 opacity-0';
export const containerLabelClassnames = (disabled?: boolean): Argument[] => [
  'text-b2 relative mb-0 min-h-lg flex cursor-pointer',
  { 'pointer-events-none': disabled },
];

type Props = FormControlProps<any>;
export const GenericFormControlContainer: React.FC<Props> = ({
  className,
  label,
  children,
  ...rest
}) => (
  <label className={classNames(className, containerLabelClassnames(rest.disabled))}>
    <div className="inline-flex items-center gap-xs group text-primary">
      <input className={classNames(hiddenInputClassNames, 'size-full!')} {...rest} />
      {children}
      {label ? <span className="[&_a]:relative">{label}</span> : null}
    </div>
  </label>
);
